import { createSelector, createSlice } from "@reduxjs/toolkit"
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../common/reducers/utilities"
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = o => o.id;

export const clientOrientationSlice = createSlice({
    name: 'clientOrientations',
    initialState: {
        data: {}
    },
    reducers: {
        setForClient: (state, action) => {
            const clientids = new Set(ensureArray(action.payload.ids));
            const toRemove = Object.values(state.data).filter(o => clientids.has(o.clientid));
            toRemove.forEach(o => delete state.data[key(o)]);
            ensureArray(action.payload.data).forEach(o => state.data[key(o)] = o);
        },
        setForMember: (state, action) => {
            const memberids = new Set(ensureArray(action.payload.ids));
            const toRemove = Object.values(state.data).filter(o => memberids.has(o.memberid));
            toRemove.forEach(o => delete state.data[key(o)]);
            ensureArray(action.payload.data).forEach(o => state.data[key(o)] = o);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(o => state.data[key(o)] = o);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(o => delete state.data[key(o)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => clientOrientationSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'clientOrientations', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'clientOrientations');

const fetchClientOrientations = clientid => network => network.get('/api/v1/clientOrientations/' + encodeURIComponent(clientid), data => clientOrientationSlice.actions.setForClient({ ids: [clientid], data }), visualizeFailure('get'));
const fetchClientOrientationsForMember = memberid => network => network.get('/api/v1/clientMemberOrientations/' + encodeURIComponent(memberid), data => clientOrientationSlice.actions.setForMember({ ids: [memberid], data }), visualizeFailure('get'));

export const createClientOrientation = (network, orientation) => network.post('/api/v1/clientOrientation', orientation, clientOrientationSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateClientOrientation = (network, delta) => network.put('/api/v1/clientOrientation', delta, clientOrientationSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeClientOrientation = (network, id) => network.delete('/api/v1/clientOrientation/' + encodeURIComponent(id), clientOrientationSlice.actions.remove(id), visualizeFailure('remove'))
// #engregion

// #region selectors
const selectLocationOrientationData = state => state.clientOrientations.data;
const selectClientOrientationClientData = createSelector([selectLocationOrientationData], data => groupBy(Object.values(data), 'clientid'));
const selectClientOrientationMemberData = createSelector([selectLocationOrientationData], data => groupBy(Object.values(data), 'memberid'));
const selectAllClientOrientations = createSelector([selectLocationOrientationData], orientations => Object.values(orientations));
const selectClientOrientations = clientid => createSelector([selectClientOrientationClientData], (orientations) => orientations[clientid] ?? []);
const selectMemberClientOrientations = memberid => createSelector([selectClientOrientationMemberData], (orientations) => orientations[memberid] ?? []);
// #endregion

const listen = {
    clientOrientationCreated: (orientation, { dispatch, websocket }) => {
        markFetched(websocket, 'client_orientation', key(orientation))
        dispatch(clientOrientationSlice.actions.addOrUpdate(orientation))
    },
    clientOrientationUpdated: (orientation, { dispatch }) => dispatch(clientOrientationSlice.actions.addOrUpdate(orientation)),
    clientOrientationRemoved: (orientation, { dispatch }) => dispatch(clientOrientationSlice.actions.remove(orientation))
}

const clientOrientationInit = initMapFactory(fetchClientOrientations, listen);
registerEntityBasedFetch('client', clientOrientationInit);

const memberClientOrientationInit = initMapFactory(fetchClientOrientationsForMember, listen);
registerEntityBasedFetch('member', memberClientOrientationInit);


const cleanup = { root: clientOrientationSlice.name, all: selectAllClientOrientations, removeAll: clientOrientationSlice.actions.remove, key };



const fullRoles = ['administrator', 'event manager', 'template manager'];
const viewRoles = ['administrator', 'event manager', 'template manager', 'auditor', 'event auditor', 'template auditor'];

const checkPermissions = (credentials, member, requiredRoles, condition) => {
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location});
    return (access && condition !== false && !member?.inactive) ?? false 
}


const options = {
    cleanup: {  root: clientOrientationSlice.name, all: selectAllClientOrientations, removeAll: clientOrientationSlice.actions.remove, key },
    condition: credentials => {
        return canViewClientOrientations(credentials)
    }
}
const singleOptions = { ...options, defaultValue: null };


export const canUpdateClientOrientations = (credentials, member) => checkPermissions(credentials, member, fullRoles, !member.inactive);
export const canCreateClientOrientations = canUpdateClientOrientations;
export const canRemoveClientOrientations = canUpdateClientOrientations;
export const hideClientOrientations = (credentials, member) => !checkPermissions(credentials, member, viewRoles);
export const canViewClientOrientations = (credentials, member) => checkPermissions(credentials, member, viewRoles);

export const [useClientOrientations] = dataHookFactory(selectClientOrientations, clientOrientationInit, {cleanup});
export const [useClientOrientationsForMember] = dataHookWithCredentialsFactory(selectMemberClientOrientations, memberClientOrientationInit, singleOptions);


const reducer = { name: clientOrientationSlice.name, reducer: clientOrientationSlice.reducer }
export default reducer;